<template>
  <section class="flex flex-col gap-4 text-left" data-testid="qualification-requirement-section">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="max-w-[640px]">
        <h3 class="flex items-center gap-2 text-base font-bold text-[#163D45]">
          Qualificação exigida
          <span class="rounded-full bg-primary5 px-2 py-0.5 text-xs font-bold text-primary40">
            Novo
          </span>
        </h3>
        <p class="mt-1 text-sm text-gray-600">
          Quais selos e documentos (aprovados no Drive do Médico) o médico precisa ter para ser
          alocado nesta escala, e o que fazer se não tiver.
        </p>
      </div>
      <OutlineButton
        v-if="!catalog.isUnavailable"
        text="Copiar requisitos de outra escala"
        height="40px"
        width="auto"
        class="px-4"
        data-testid="qualification-copy-open"
        @click="copyOpen = true"
      />
    </div>

    <p
      v-if="catalog.isUnavailable"
      class="rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm"
      data-testid="qualification-unavailable"
    >
      A qualificação exigida ainda não foi liberada neste ambiente.
    </p>

    <template v-else>
      <p
        v-if="notice"
        class="rounded-lg border border-alert bg-orange-50 px-3 py-2 text-sm"
        data-testid="qualification-notice"
      >
        {{ notice }}
      </p>
      <p
        v-if="copied"
        class="rounded-lg border border-sucess bg-green-50 px-3 py-2 text-sm"
        data-testid="qualification-copied-notice"
      >
        Requisitos copiados de outra escala. A partir de agora esta escala tem uma cópia própria.
      </p>

      <template v-if="mode === 'shortcuts'">
        <p class="text-sm text-gray-600">
          Marque o que o médico precisa ter. Itens marcados são exigidos <strong>juntos</strong>.
        </p>

        <div class="qr-card" :class="{ 'qr-card--on': shortcuts.minimumDocs }">
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <Checkbox
              field-id="qualification-shortcut-minimum-docs"
              label="Documentação Mínima"
              :checked="shortcuts.minimumDocs"
              data-testid="qualification-shortcut-minimum-docs"
              @update:checked="onMinimumDocs"
            />
            <p class="pl-8 text-xs text-gray-500">{{ minimumDocsDescription }}</p>
          </div>
          <OnFailSelector
            v-if="shortcuts.minimumDocs"
            v-model="shortcuts.minimumDocsOnFail"
            test-id="qualification-shortcut-minimum-docs-on-fail"
          />
        </div>

        <div
          class="qr-card"
          :class="{ 'qr-card--on': shortcuts.lifeSupport, 'qr-card--error': lifeSupportError }"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-2">
            <Checkbox
              field-id="qualification-shortcut-life-support"
              label="Suporte a Vida"
              :checked="shortcuts.lifeSupport"
              data-testid="qualification-shortcut-life-support"
              @update:checked="onLifeSupport"
            />
            <p class="pl-8 text-xs text-gray-500">Certificados ATLS, ACLS e PALS</p>
            <div v-if="shortcuts.lifeSupport" class="flex flex-col gap-2 pl-8">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-xs font-semibold text-gray-600">Exigir</span>
                <SegmentedControl
                  :model-value="shortcuts.lifeSupportMode"
                  :options="LIFE_SUPPORT_MODES"
                  ariaLabel="Como combinar os certificados"
                  size="sm"
                  test-id="qualification-shortcut-life-support-mode"
                  @update:model-value="onLifeSupportMode"
                />
              </div>
              <div class="flex flex-wrap gap-4">
                <Checkbox
                  v-for="key in LIFE_SUPPORT_KEYS"
                  :key="key"
                  :field-id="`qualification-shortcut-life-support-${key}`"
                  :label="catalog.typeName(key)"
                  :value="key"
                  :checked="shortcuts.lifeSupportKeys.includes(key)"
                  :data-testid="`qualification-shortcut-life-support-${key}`"
                  @update:checked="onLifeSupportKey"
                />
              </div>
              <p
                v-if="lifeSupportError"
                class="text-xs text-negative"
                data-testid="qualification-shortcut-life-support-error"
              >
                Escolha ao menos um certificado (ATLS, ACLS ou PALS) ou desmarque Suporte a Vida.
              </p>
            </div>
          </div>
          <OnFailSelector
            v-if="shortcuts.lifeSupport"
            v-model="shortcuts.lifeSupportOnFail"
            test-id="qualification-shortcut-life-support-on-fail"
          />
        </div>

        <div class="qr-card opacity-60">
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <Checkbox
              field-id="qualification-shortcut-specialist"
              label="Especialista"
              :checked="false"
              disabled
              data-testid="qualification-shortcut-specialist"
            />
            <p class="pl-8 text-xs text-gray-500">RQE na especialidade</p>
          </div>
          <LabelBadge label="Em breve" color="gray" size="small" :icon="null" />
        </div>

        <div class="flex flex-col gap-2">
          <h4 class="text-sm font-bold text-[#163D45]">Outros documentos exigidos</h4>
          <div
            v-for="(doc, index) in shortcuts.extraDocuments"
            :key="doc.key"
            class="qr-card qr-card--on"
            :data-testid="`qualification-extra-${doc.key}`"
          >
            <span class="flex-1 text-sm font-semibold">{{ catalog.typeName(doc.key) }}</span>
            <OnFailSelector
              v-model="doc.onFail"
              :test-id="`qualification-extra-${doc.key}-on-fail`"
            />
            <v-btn
              icon
              variant="text"
              size="small"
              density="compact"
              :aria-label="`Remover ${catalog.typeName(doc.key)}`"
              :data-testid="`qualification-extra-${doc.key}-remove`"
              @click="shortcuts.extraDocuments.splice(index, 1)"
            >
              <v-icon :icon="mdiClose" size="20" />
            </v-btn>
          </div>
          <div class="max-w-[360px]">
            <v-autocomplete
              :model-value="null"
              :items="availableDocuments"
              label="Adicionar documento do catálogo"
              variant="outlined"
              density="compact"
              color="primary20"
              hide-details
              :prepend-inner-icon="mdiPlus"
              no-data-text="Nenhum tipo disponível"
              data-testid="qualification-extra-add"
              @update:model-value="addExtraDocument"
            />
          </div>
        </div>

        <button
          type="button"
          class="self-start text-sm font-semibold text-primary30 hover:underline"
          data-testid="qualification-go-advanced"
          @click="goAdvanced"
        >
          Modo avançado — combinar critérios com E/OU
        </button>
      </template>

      <template v-else>
        <div
          class="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#F0F0F4] px-3 py-2 text-sm"
          data-testid="qualification-advanced-banner"
        >
          <span>
            <strong>Modo avançado.</strong>
            <template v-if="!canGoBack">
              Esta política usa combinações (grupos E/OU) que os atalhos não mostram.
            </template>
          </span>
          <button
            type="button"
            class="text-sm font-semibold text-primary30 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
            :disabled="!canGoBack"
            :title="canGoBack ? '' : 'Remova os subgrupos para voltar aos atalhos'"
            data-testid="qualification-back-to-shortcuts"
            @click="backToShortcuts"
          >
            Voltar aos atalhos
          </button>
        </div>
        <PolicyBuilder v-model="advanced" />
      </template>

      <PolicySummary :policy="policy" :errors="errors" />
    </template>

    <CopyRequirementsModal
      :modal-active="copyOpen"
      :has-current-requirements="!isPolicyEmpty(policy)"
      @close="copyOpen = false"
      @copy="onCopy"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { mdiClose, mdiPlus } from '@mdi/js'

import Checkbox from '@/components/designSystem/Checkbox/index.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import SegmentedControl from '@/components/designSystem/SegmentedControl/SegmentedControl.vue'
import type { SegmentedOption } from '@/components/designSystem/SegmentedControl/SegmentedControl.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import {
  CATALOG_LOAD_ERROR,
  useProfessionalDocumentCatalogStore
} from '@/pinia-store/useProfessionalDocumentCatalogStore'
import { ProfessionalSealKey } from '@/types/professional-documents.types'
import { QualificationOnFail } from '@/types/qualification-eligibility.types'
import type { QualificationRuleGroup } from '@/types/qualification-policy.types'

import CopyRequirementsModal from './CopyRequirementsModal.vue'
import OnFailSelector from './OnFailSelector.vue'
import PolicyBuilder from './PolicyBuilder.vue'
import PolicySummary from './PolicySummary.vue'
import {
  LIFE_SUPPORT_KEYS,
  clonePolicy,
  emptyShortcuts,
  isPolicyEmpty,
  policyToShortcuts,
  shortcutErrors,
  shortcutsToPolicy,
  validatePolicy
} from './policyModel'
import type { LifeSupportMode, RequirementShortcuts } from './policyModel'

type CheckboxEvent = { isChecked: boolean; value: string | null }

const props = withDefaults(
  defineProps<{ modelValue: QualificationRuleGroup; notice?: string | null }>(),
  { notice: null }
)
const emit = defineEmits<{
  'update:modelValue': [value: QualificationRuleGroup]
  validityChange: [valid: boolean]
  /**
   * Política (e empresa) de origem do "Copiar requisitos" — o Editar manda como
   * `copiedFromPolicyId` (A3.5) quando a empresa é a mesma (R51.4).
   */
  copiedFrom: [sourcePolicyId: number, sourceCompanyId: number | null]
}>()

const catalog = useProfessionalDocumentCatalogStore()
const { notifyError } = useErrorToast()
// Sem catálogo os nomes caem na chave e o autocomplete fica vazio; a falha vira toast. O 404 da
// flag desligada não lança: a store marca `isUnavailable` e a seção mostra o aviso.
catalog.ensureLoaded().catch((error) => notifyError(error, CATALOG_LOAD_ERROR))

const LIFE_SUPPORT_MODES: SegmentedOption[] = [
  { value: 'ANY', label: 'Pelo menos um' },
  { value: 'ALL', label: 'Todos' }
]

const mode = ref<'shortcuts' | 'advanced'>('shortcuts')
const shortcuts = ref<RequirementShortcuts>(emptyShortcuts())
const advanced = ref<QualificationRuleGroup>(clonePolicy(props.modelValue))
const copyOpen = ref(false)
const copied = ref(false)

/** Política que os atalhos não expressam abre direto no modo avançado (Review Focus 2). */
function load(next: QualificationRuleGroup) {
  const asShortcuts = policyToShortcuts(next)
  mode.value = asShortcuts ? 'shortcuts' : 'advanced'
  shortcuts.value = asShortcuts ?? emptyShortcuts()
  advanced.value = clonePolicy(next)
}
load(props.modelValue)

const policy = computed<QualificationRuleGroup>(() =>
  mode.value === 'shortcuts' ? shortcutsToPolicy(shortcuts.value) : advanced.value
)
const lifeSupportError = computed(
  () => mode.value === 'shortcuts' && shortcutErrors(shortcuts.value).length > 0
)
// Nos atalhos também vale o limite de largura do backend (muitos documentos avulsos na raiz).
const errors = computed(() =>
  mode.value === 'shortcuts'
    ? [...shortcutErrors(shortcuts.value), ...validatePolicy(policy.value)]
    : validatePolicy(advanced.value)
)
const canGoBack = computed(() => policyToShortcuts(advanced.value) !== null)

const minimumDocsDescription = computed(
  () =>
    catalog.seals.find((seal) => seal.key === ProfessionalSealKey.MINIMUM_DOCS)?.description ||
    'Identidade, CRM, Diploma e certidões Concluídos + CRM Ativo na base'
)
const availableDocuments = computed(() =>
  catalog.types
    .filter((type) => !shortcuts.value.extraDocuments.some((doc) => doc.key === type.key))
    .map((type) => ({ title: type.name, value: type.key }))
)

/** Só emite o que mudou de fato (evita laço com o `watch` do `modelValue`). */
let lastEmitted = JSON.stringify(props.modelValue)
watch(
  policy,
  (next) => {
    const serialized = JSON.stringify(next)
    if (serialized === lastEmitted) return
    lastEmitted = serialized
    emit('update:modelValue', clonePolicy(next))
  },
  { deep: true }
)
watch(
  () => props.modelValue,
  (next) => {
    const serialized = JSON.stringify(next)
    if (serialized === lastEmitted) return
    lastEmitted = serialized
    load(next)
  },
  { deep: true }
)
watch(
  () => errors.value.length === 0,
  (valid) => emit('validityChange', valid),
  { immediate: true }
)

const onMinimumDocs = (event: CheckboxEvent) => (shortcuts.value.minimumDocs = event.isChecked)
const onLifeSupport = (event: CheckboxEvent) => (shortcuts.value.lifeSupport = event.isChecked)
// O SegmentedControl só oferece as opções de `LIFE_SUPPORT_MODES`.
const onLifeSupportMode = (value: string) =>
  (shortcuts.value.lifeSupportMode = value as LifeSupportMode)

/** Mantém a ordem ATLS, ACLS, PALS (o vai-e-volta com a árvore fica estável). */
function onLifeSupportKey({ isChecked, value }: CheckboxEvent) {
  if (!value) return
  const selected = shortcuts.value.lifeSupportKeys
  const next = isChecked ? [...selected, value] : selected.filter((item) => item !== value)
  shortcuts.value.lifeSupportKeys = LIFE_SUPPORT_KEYS.filter((item) => next.includes(item))
}

function addExtraDocument(key: string | null) {
  if (!key) return
  shortcuts.value.extraDocuments.push({ key, onFail: QualificationOnFail.BLOCK })
}

function goAdvanced() {
  advanced.value = shortcutsToPolicy(shortcuts.value)
  mode.value = 'advanced'
}

function backToShortcuts() {
  const asShortcuts = policyToShortcuts(advanced.value)
  if (!asShortcuts) return
  shortcuts.value = asShortcuts
  mode.value = 'shortcuts'
}

function onCopy(
  rules: QualificationRuleGroup,
  sourcePolicyId: number,
  sourceCompanyId: number | null
) {
  load(rules)
  copied.value = true
  copyOpen.value = false
  emit('copiedFrom', sourcePolicyId, sourceCompanyId)
}
</script>

<style>
#app .qr-card {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #d8d8e2;
  border-radius: 8px;
  background: #fff;
}
#app .qr-card--on {
  border-color: #84c8d7;
  background: #f3f9fb;
}
#app .qr-card--error {
  border-color: #da1e28;
  background: #fff1f1;
}
</style>
